import { Card, CardContent, IconButton, Stack, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { DynamicForm, DynamicIcon, EllipsesMenu, Tooltip } from 'src/components';
import { StyledCardHeader } from 'src/components/SidePanelTabs/SiteClient/client-card';
import { SiteAddress } from 'src/components/SiteComponents/site-address';
import { Address, Client, useAddress, useLabelToCta } from 'src/graphql';
import { useActivationFlowUrl } from 'src/graphql/entities/activation-flow-url';
import { EntityNames } from 'src/graphql/types';
import { useUpdateAssociatedSite } from 'src/pages/Sites/use-update-associated-site';
import { Modals, useModalMethods } from 'src/store/modals';
import { useConfirmationModal, useCopyToClipboard, useOpenSite } from 'src/utils/hooks';
import { AssociatedSiteFormValues, useAssociatedSiteForm } from './use-associated-site-form';

export const SiteCard = ({
  address,
  client,
  viewOnly = false,
  controls,
}: {
  address: Address;
  client?: Client;
  viewOnly?: boolean;
  controls?: React.ReactNode;
}) => {
  const theme = useTheme();
  const { confirm } = useConfirmationModal();
  const { showModal } = useModalMethods(Modals.AddSiteModal);

  const { associatedSiteFormFields } = useAssociatedSiteForm(address, viewOnly);

  const { updateAddress, deleteClientToAddress } = useAddress(address?.addressId);
  const { deleteLabelToCta } = useLabelToCta();

  const { generateAfMagicLoginUrl } = useActivationFlowUrl();

  const openSite = useOpenSite();

  const { copyToClipboard } = useCopyToClipboard();

  const updateAssociatedSite = useUpdateAssociatedSite(address);

  const onSubmitAssociatedSite = useCallback(
    (values: AssociatedSiteFormValues) => {
      void updateAssociatedSite(values);
    },
    [updateAssociatedSite],
  );

  const handleCopyActivationFlowUrl = useCallback(async () => {
    if (
      !client?.clientId ||
      !client.primaryEmail ||
      !address?.addressId ||
      !address.partnerId
    ) {
      return;
    }

    const url = await generateAfMagicLoginUrl({
      addressId: address.addressId,
      partnerId: address.partnerId,
      clientId: client.clientId,
      email: client.primaryEmail,
      disableLink: true,
    });

    if (url?.url) {
      await copyToClipboard(url.url);
    }
  }, [
    address?.addressId,
    address?.partnerId,
    client?.clientId,
    client?.primaryEmail,
    copyToClipboard,
    generateAfMagicLoginUrl,
  ]);

  const menuOptions = useMemo(() => {
    const options = [];
    if (client) {
      options.push({
        label: 'Update Site',
        icon: 'edit',
        onClick: () => {
          showModal({
            client: client
              ? {
                  partnerId: client.partnerId,
                  id: client.clientId,
                  name: client.fullName,
                  email: client.primaryEmail,
                }
              : undefined,
            address,
          });
        },
      });
      options.push({
        label: 'Disassociate Site',
        icon: 'delete',
        onClick: async () => {
          if (
            await confirm({
              body: 'Please confirm you want to disassociate the site.\nThis process cannot be undone.',
              variant: 'delete',
            })
          ) {
            if (address?.primaryClientId === client.clientId) {
              await updateAddress({
                ...address,
                primaryClientId: null,
                primaryClient: {
                  clientId: null,
                },
              });
            }
            await deleteLabelToCta({
              clientId: client.clientId,
              addressId: address?.addressId,
            });
            await deleteClientToAddress(
              client.clientId,
              address?.partner?.partnerId ?? address?.partnerId,
              EntityNames.Site,
            );
          }
        },
      });
      options.push({
        label: 'Copy Client ID',
        icon: 'content_copy',
        onClick: async () => {
          await copyToClipboard(client.clientId);
        },
      });
    }
    return options;
  }, [
    address,
    client,
    confirm,
    deleteLabelToCta,
    deleteClientToAddress,
    showModal,
    copyToClipboard,
    updateAddress,
  ]);

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: theme.palette.custom.OV.sys.outlineVariant,
      }}
    >
      <StyledCardHeader
        sx={{
          '& .MuiCardHeader-content': {
            overflow: 'hidden',
          },
        }}
        title={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            overflow="hidden"
            width="100%"
          >
            <SiteAddress
              address={address}
              onClick={
                viewOnly
                  ? undefined
                  : (event: React.MouseEvent<HTMLElement>) => {
                      openSite(event, address);
                    }
              }
              tooltipText="Click to open site"
            />
            {!viewOnly && (
              <Tooltip
                hideWhenRefIsHidden
                title={
                  <Stack direction="column" alignItems="center">
                    <span>{address.fullAddress}</span>
                    <span>Click to copy</span>
                  </Stack>
                }
              >
                <IconButton
                  aria-label="settings"
                  onClick={async () => {
                    if (!address.fullAddress) {
                      return;
                    }
                    await copyToClipboard(address.fullAddress);
                  }}
                >
                  <DynamicIcon name="content_copy" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
        titleTypographyProps={{
          variant: 'subtitle2',
        }}
        action={
          <>
            {!viewOnly && (
              <Tooltip title="Copy Client Activation URL">
                <IconButton
                  aria-haspopup="true"
                  size="small"
                  sx={{
                    padding: 0,
                  }}
                  onClick={handleCopyActivationFlowUrl}
                >
                  <DynamicIcon name="share" />
                </IconButton>
              </Tooltip>
            )}
            {!viewOnly && !!menuOptions?.length && <EllipsesMenu options={menuOptions} />}
            {controls}
          </>
        }
      />
      <CardContent
        sx={{
          padding: theme.utils.important(theme.spacing(1)),
        }}
      >
        <DynamicForm<AssociatedSiteFormValues>
          watchable
          watchableSubmitOnlyChangedField
          fields={associatedSiteFormFields}
          onSubmit={onSubmitAssociatedSite}
        />
      </CardContent>
    </Card>
  );
};
